/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */

import moment from 'moment-timezone';

export const TYPE_EARLY_LEAVE = 1;
export const TYPE_LATE_ARRIVAL = 2;

const padDateTime = (value) => String(value).padStart(2, '0');

export const parseServerDateTime = (value) => {
  if (!value) {
    return null;
  }
  return new Date(`${value.replace(' ', 'T')}Z`);
};

export const formatLocalDateTime12h = (date) => {
  const localDate = [
    date.getFullYear(),
    padDateTime(date.getMonth() + 1),
    padDateTime(date.getDate()),
  ].join('-');
  const localTime = formatLocalTime12h(date);
  return `${localDate} ${localTime}`;
};

export const formatLocalTime12h = (date) => {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

export const formatStoredUserDateTime = (value) => {
  if (!value) {
    return null;
  }
  const parsed = moment(value, 'YYYY-MM-DD HH:mm:ss', true);
  if (!parsed.isValid()) {
    return value;
  }
  return formatLocalDateTime12h(parsed.toDate());
};

export function getBrowserTimezone() {
  return moment.tz.guess();
}

export function formatTimezoneLabel(timezoneName) {
  const name = timezoneName || getBrowserTimezone();
  const offset = moment.tz(name).format('Z');
  return `(UTC${offset}) ${name}`;
}

const resolveLeaveDate = (leaveDate) => {
  if (typeof leaveDate === 'function') {
    return leaveDate();
  }
  return leaveDate;
};

const normalizeTime = (time) => {
  if (!time) {
    return null;
  }
  return time.length === 5 ? `${time}:00` : time;
};

const buildShiftStartMoment = (leaveDate, shiftStartTime, workTimezone) => {
  const time = normalizeTime(shiftStartTime);
  if (!leaveDate || !time || !workTimezone) {
    return null;
  }
  return moment.tz(`${leaveDate} ${time}`, 'YYYY-MM-DD HH:mm:ss', workTimezone);
};

const buildShiftEndMoment = (
  leaveDate,
  shiftStartTime,
  shiftEndTime,
  workTimezone,
) => {
  const start = buildShiftStartMoment(leaveDate, shiftStartTime, workTimezone);
  const endTime = normalizeTime(shiftEndTime);
  if (!start || !endTime || !workTimezone) {
    return null;
  }
  const end = moment.tz(
    `${leaveDate} ${endTime}`,
    'YYYY-MM-DD HH:mm:ss',
    workTimezone,
  );
  if (end.isSameOrBefore(start)) {
    end.add(1, 'day');
  }
  return end;
};

const buildPermissionTimeMoment = (
  leaveDate,
  permissionTime,
  shiftStartTime,
  shiftEndTime,
  workTimezone,
) => {
  const start = buildShiftStartMoment(leaveDate, shiftStartTime, workTimezone);
  const time = normalizeTime(permissionTime);
  if (!start || !time || !workTimezone) {
    return null;
  }
  const resolved = moment.tz(
    `${leaveDate} ${time}`,
    'YYYY-MM-DD HH:mm:ss',
    workTimezone,
  );
  if (resolved.isSameOrBefore(start)) {
    resolved.add(1, 'day');
  }
  return resolved;
};

const buildExpectedMoment = (leaveDate, expectedTime, timezoneName) => {
  const time = normalizeTime(expectedTime);
  if (!leaveDate || !time) {
    return null;
  }
  return moment.tz(
    `${leaveDate} ${time}`,
    'YYYY-MM-DD HH:mm:ss',
    timezoneName || getBrowserTimezone(),
  );
};

export function buildSubmitPayload({leaveDate, expectedTime, comment}) {
  const timezoneName = getBrowserTimezone();
  const normalizedTime = normalizeTime(expectedTime);
  const localMoment = buildExpectedMoment(
    leaveDate,
    normalizedTime,
    timezoneName,
  );
  const offsetHours = localMoment
    ? String(localMoment.utcOffset() / 60)
    : String(moment.tz(timezoneName).utcOffset() / 60);

  return {
    leaveDate,
    expectedTime: normalizedTime?.slice(0, 5) ?? expectedTime,
    timezoneName,
    timezoneOffset: offsetHours,
    comment: comment || undefined,
  };
}

export function validateEarlyLeaveDeparture(
  leaveDate,
  expectedTime,
  {
    shiftStartTime,
    shiftEndTime,
    workTimezone,
    minEarlyDepartureTime,
    earlyLeaveMinOffsetSeconds,
  },
) {
  const expected = buildExpectedMoment(
    leaveDate,
    expectedTime,
    getBrowserTimezone(),
  );
  const clockOut = buildShiftEndMoment(
    leaveDate,
    shiftStartTime,
    shiftEndTime,
    workTimezone,
  );

  if (!expected?.isValid() || !clockOut?.isValid()) {
    return false;
  }

  const allowedStart = minEarlyDepartureTime
    ? buildPermissionTimeMoment(
        leaveDate,
        minEarlyDepartureTime,
        shiftStartTime,
        shiftEndTime,
        workTimezone,
      )
    : clockOut
        .clone()
        .subtract(Number(earlyLeaveMinOffsetSeconds) || 0, 'seconds');

  if (!allowedStart?.isValid()) {
    return false;
  }

  return (
    expected.isSameOrAfter(allowedStart) && expected.isSameOrBefore(clockOut)
  );
}

export function validateLateArrival(
  leaveDate,
  expectedTime,
  {
    shiftStartTime,
    shiftEndTime,
    workTimezone,
    maxLateArrivalTime,
    lateArrivalMaxMinutes,
  },
) {
  const expected = buildExpectedMoment(
    leaveDate,
    expectedTime,
    getBrowserTimezone(),
  );
  const clockIn = buildShiftStartMoment(
    leaveDate,
    shiftStartTime,
    workTimezone,
  );
  const clockOut = buildShiftEndMoment(
    leaveDate,
    shiftStartTime,
    shiftEndTime,
    workTimezone,
  );

  if (!expected?.isValid() || !clockIn?.isValid() || !clockOut?.isValid()) {
    return false;
  }

  const maxArrival = maxLateArrivalTime
    ? buildPermissionTimeMoment(
        leaveDate,
        maxLateArrivalTime,
        shiftStartTime,
        shiftEndTime,
        workTimezone,
      )
    : clockIn.clone().add(Number(lateArrivalMaxMinutes) || 0, 'minutes');
  if (!maxArrival?.isValid()) {
    return false;
  }
  const maxAllowed = moment.min(clockOut, maxArrival);

  return expected.isAfter(clockIn) && expected.isSameOrBefore(maxAllowed);
}

export function formatShiftTime12h(time) {
  const normalized = normalizeTime(time);
  if (!normalized) {
    return '';
  }
  const parsed = moment(normalized, 'HH:mm:ss', true);
  return parsed.isValid() ? parsed.format('h:mm A') : time;
}

export function createTimeRangeValidator(type, scheduleProps) {
  return (expectedTime) => {
    const leaveDate = resolveLeaveDate(scheduleProps.leaveDate);
    if (!leaveDate || !expectedTime) {
      return true;
    }

    const isValid =
      type === 'early_leave'
        ? validateEarlyLeaveDeparture(leaveDate, expectedTime, scheduleProps)
        : validateLateArrival(leaveDate, expectedTime, scheduleProps);

    if (isValid) {
      return true;
    }

    const errorMessage =
      type === 'early_leave'
        ? scheduleProps.departureErrorMessage
        : scheduleProps.arrivalErrorMessage;

    return typeof errorMessage === 'function' ? errorMessage() : errorMessage;
  };
}

export function resolveExpectedTimeDisplay(item) {
  const isEarlyLeave = item.type === TYPE_EARLY_LEAVE;

  if (isEarlyLeave) {
    const userDisplay = formatStoredUserDateTime(
      item.expectedDepartureUserTime,
    );
    if (userDisplay) {
      return userDisplay;
    }
    const utc = parseServerDateTime(item.expectedDepartureUtcTime);
    return utc ? formatLocalDateTime12h(utc) : '-';
  }

  const userDisplay = formatStoredUserDateTime(item.expectedArrivalUserTime);
  if (userDisplay) {
    return userDisplay;
  }
  const utc = parseServerDateTime(item.expectedArrivalAt);
  return utc ? formatLocalDateTime12h(utc) : '-';
}

const formatStoredUserTime12h = (value) => {
  if (!value) {
    return null;
  }
  const parsed = moment(value, 'YYYY-MM-DD HH:mm:ss', true);
  if (!parsed.isValid()) {
    return value;
  }
  return formatLocalTime12h(parsed.toDate());
};

export function resolveDashboardShiftPermissionTime(item) {
  if (item.source === 'early_leave') {
    const userTime = formatStoredUserTime12h(item.expectedDepartureUserTime);
    if (userTime) {
      return userTime;
    }
    const utc = parseServerDateTime(item.expectedDepartureUtcTime);
    if (utc) {
      return formatLocalTime12h(utc);
    }
    return item.startTime || null;
  }

  if (item.source === 'late_arrival') {
    const userTime = formatStoredUserTime12h(item.expectedArrivalUserTime);
    if (userTime) {
      return userTime;
    }
    const utc = parseServerDateTime(item.expectedArrivalAt);
    if (utc) {
      return formatLocalTime12h(utc);
    }
    return item.startTime || null;
  }

  return null;
}

/**
 * Dashboard line for shift permissions.
 * Planned: "Oct 12 • Early (4:00 PM)" — timeStyle parens + optional date.
 * Today: "Leaving Early (4:00 PM)" / "Arriving Late (9:00 AM)" — parens time, no date.
 */
export function formatDashboardShiftPermissionLine(
  item,
  {
    formattedDate = null,
    labelEarly = 'Early',
    labelLate = 'Late',
    timeStyle = 'parens',
  } = {},
) {
  if (item.source !== 'early_leave' && item.source !== 'late_arrival') {
    return null;
  }

  const label = item.source === 'early_leave' ? labelEarly : labelLate;
  const time = resolveDashboardShiftPermissionTime(item);
  let timeSuffix = '';
  if (time) {
    timeSuffix = timeStyle === 'bullet' ? ` • ${time}` : ` (${time})`;
  }

  if (formattedDate) {
    return `${formattedDate} • ${label}${timeSuffix}`;
  }

  return `${label}${timeSuffix}`;
}
